import { type DSButtonProps, DSSpacer, DSText } from '@zoe/ds-web'
import { Fragment, useCallback, useContext, type FC } from 'react'

import type { FragmentComponent } from '@/types/graphql'

import { TrackingContext } from '@/context/trackingContext'

import { DSLinkButton } from '@/components/ds/LinkButton/LinkButton'

import { ctaCollectionBlock } from '@/lib/api/graphql/utils/commonBlocks'
import { useCtaHref } from '@/lib/experiments/ctaHrefOverrides'
import { trackCtaClick } from '@/lib/gtm/gtmV2'

export type CtaRecord = {
  __typename: 'CtaRecord'
  id: string
  label: string
  url: string
  newTab?: boolean
  variant?: 'primary' | 'secondary' | 'tertiary' | 'google_play' | 'app_store'
  width?: DSButtonProps['width']
  /** Classic schemes drive the legacy Cta; the next-gen values drive next-gen CTAs. Each ignores the other's. */
  colorScheme?: DSButtonProps['colorScheme'] | 'brand' | 'accent' | 'contrast'
  noWrap?: boolean
  dataTestId?: string
}

export type CtaCollectionRecord = CtaRecord[]

interface CtaProps {
  record: CtaRecord
  onPress?: () => void
}

export const Cta: FC<CtaProps> = ({ record, onPress }) => {
  const { section } = useContext(TrackingContext)
  // Authored destination unless a running experiment owns this CTA.
  const url = useCtaHref(record.url, record.dataTestId)
  const handleOnPress = useCallback(
    ({ label }: { label: string }) => {
      trackCtaClick(section || 'cms', record.dataTestId || label || '')
      onPress?.()
    },
    [section, record.dataTestId, onPress],
  )
  const noWrapStyle = record.noWrap ? { whiteSpace: 'nowrap' } : {}

  if (record.variant === 'app_store') {
    return (
      <a
        href={url || 'https://apps.apple.com/us/app/zoe-insights/id1471632228'}
        target={record.newTab ? '_blank' : '_self'}
        rel="noopener noreferrer"
        onClick={() => handleOnPress({ label: record.label || 'App Store Download' })}
      >
        <img
          src="/assets/img/app/AppStore.png"
          alt={record.label || 'Download on the App Store'}
          style={{ display: 'block', width: '166px', marginBottom: '16px' }}
        />
      </a>
    )
  }

  if (record.variant === 'google_play') {
    return (
      <a
        href={url || 'https://play.google.com/store/apps/details?id=com.joinzoe.results'}
        target={record.newTab ? '_blank' : '_self'}
        rel="noopener noreferrer"
        onClick={() => handleOnPress({ label: record.label || 'Google Play Download' })}
      >
        <img
          src="/assets/img/app/PlayStore.png"
          alt={record.label || 'Get it on Google Play'}
          style={{ display: 'block', width: '166px', marginBottom: '16px' }}
        />
      </a>
    )
  }

  const classicScheme =
    record.colorScheme === 'default' || record.colorScheme === 'inverse' || record.colorScheme === 'neutral'
      ? record.colorScheme
      : 'default'

  if (record.variant === 'primary' || record.variant === 'secondary' || record.variant === 'tertiary') {
    return (
      <DSLinkButton
        variant={record.variant || 'primary'}
        colourScheme={classicScheme}
        size="large"
        width={record.width || 'auto'}
        href={url}
        target={record.newTab ? '_blank' : '_self'}
        onPress={() => handleOnPress({ label: record.label })}
        testId={record.dataTestId}
      >
        <DSText
          as="span"
          variant="fluid-paragraph-200"
          weight="semiBold"
          style={noWrapStyle}
          color={classicScheme === 'inverse' ? 'primary-inverse' : 'primary'}
        >
          {record.label}
        </DSText>
      </DSLinkButton>
    )
  }

  return null
}

export const CtaCollectionBlock: FragmentComponent<{}, CtaCollectionRecord> = ({ record }) => {
  return [...record].map((item) => (
    <Fragment key={item.id}>
      <Cta record={item} />
      <DSSpacer direction="horizontal" size={16} />
    </Fragment>
  ))
}

CtaCollectionBlock.fragment = ctaCollectionBlock
CtaCollectionBlock.recordName = 'CtaRecord'
